import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import { rupiahShort, AGING, PRIORITY } from "@/lib/format";
import LeadDetailSheet from "@/components/LeadDetailSheet";
import LeadFormDialog from "@/components/LeadFormDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Phone, Clock, LayoutList, Kanban as KanbanIcon, CalendarDays, Tag, X, Plus, TriangleAlert, Search } from "lucide-react";
import { toast } from "sonner";
import { rupiah, fmtDate } from "@/lib/format";
import PipelineCalendar from "@/components/PipelineCalendar";
import { STAGES } from "@/lib/stages";
import { pendingActivitiesByType, SCHEDULED_ACTIVITY_TYPES } from "@/lib/scheduledActivities";

const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const LABEL_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#a855f7", "#ec4899", "#14b8a6", "#71717a"];

// Darkened, hairline-thin accent gradients for the list-view stage bar — fades
// each stage color into black rather than a bright flat-color border. SPH's
// stock amber/orange is swapped for a brushed silver-metallic fade instead.
const STAGE_BAR_GRADIENT = {
  "New Client": "linear-gradient(90deg, #3d2666, rgba(0,0,0,0) 68%)",
  "Konsultasi": "linear-gradient(90deg, #17356b, rgba(0,0,0,0) 68%)",
  "Survei": "linear-gradient(90deg, #0a4e5c, rgba(0,0,0,0) 68%)",
  "SPH": "linear-gradient(90deg, #8c9096, #4a4d52 38%, #0a0a0b 72%, rgba(0,0,0,0) 100%)",
  "Closing": "linear-gradient(90deg, #0a5a40, rgba(0,0,0,0) 68%)",
  "Lost": "linear-gradient(90deg, #641f1f, rgba(0,0,0,0) 68%)",
};
const KANBAN_LIMIT = 12;

function LostReasonDialog({ pending, reasons, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  React.useEffect(() => { if (pending) setReason(""); }, [pending]);
  return (
    <Dialog open={!!pending} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="bg-[#0f0f12] border-white/10">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><TriangleAlert size={18} className="text-red-400" />Pindahkan ke Lost</DialogTitle>
          <DialogDescription>{pending ? `Kenapa "${pending.nama}" tidak lanjut?` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={reason || undefined} onValueChange={setReason}>
            <SelectTrigger data-testid="lost-reason-select" className="bg-[#18181B] border-white/10 h-10"><SelectValue placeholder="Pilih alasan lost" /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10">
              {reasons.map((r) => <SelectItem key={r.value} value={r.value}>{r.value}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 border-white/10" onClick={onCancel}>Batal</Button>
            <Button data-testid="lost-reason-confirm" className="flex-1 bg-gradient-to-r from-red-600 to-red-800" disabled={!reason} onClick={() => onConfirm(reason)}>Konfirmasi Lost</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LabelPopover({ lead, onClose }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [color, setColor] = useState(LABEL_COLORS[0]);
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/leads/${lead.lead_id}/labels`, { text: text.trim(), color });
      qc.invalidateQueries({ queryKey: ["leads"] });
      setText("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async (label_id) => {
    try { await api.delete(`/leads/${lead.lead_id}/labels/${label_id}`); qc.invalidateQueries({ queryKey: ["leads"] }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const hasFollowUp = (lead.labels || []).some((l) => l.text === "Follow Up");
  const addFollowUp = async () => {
    setBusy(true);
    try { await api.post(`/leads/${lead.lead_id}/labels`, { text: "Follow Up", color: "#ef4444" }); qc.invalidateQueries({ queryKey: ["leads"] }); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div onClick={(e) => e.stopPropagation()} className="absolute z-20 top-full left-0 mt-1 w-56 card-surface rounded-xl border border-white/10 p-3 shadow-xl">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase text-zinc-400">Label</span>
        <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={13} /></button>
      </div>
      {!!(lead.labels || []).length && (
        <div className="flex flex-wrap gap-1 mb-2">
          {lead.labels.map((l) => (
            <span key={l.label_id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: l.color + "26", color: l.color, border: `1px solid ${l.color}55` }}>
              {l.text}
              <button onClick={() => remove(l.label_id)} className="hover:opacity-70"><X size={9} /></button>
            </span>
          ))}
        </div>
      )}
      {!hasFollowUp && (
        <button data-testid={`quick-label-followup-${lead.lead_id}`} onClick={addFollowUp} disabled={busy}
          className="w-full h-7 rounded-lg mb-2 text-[11px] font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
          style={{ background: "#ef444426", color: "#ef4444", border: "1px solid #ef444455" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#ef4444" }} />+ Follow Up (cepat)
        </button>
      )}
      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
        placeholder="mis. Meeting, Survei…" className="w-full bg-[#18181B] border border-white/10 rounded-lg h-8 px-2 text-xs mb-2" />
      <div className="flex items-center gap-1.5 mb-2">
        {LABEL_COLORS.map((c) => (
          <button key={c} onClick={() => setColor(c)} className="w-5 h-5 rounded-full shrink-0" style={{ background: c, outline: color === c ? "2px solid #fff" : "none", outlineOffset: 1 }} />
        ))}
      </div>
      <button onClick={add} disabled={busy || !text.trim()} className="w-full h-7 rounded-lg bg-blue-600 text-xs font-medium disabled:opacity-50">{busy ? "…" : "+ Tambah Label"}</button>
    </div>
  );
}

// One badge PER TYPE that still has a pending schedule — a lead with both a Survei and a
// Zoom Meeting scheduled shows BOTH, side by side. Picking only the single overall-soonest
// activity used to make a 2nd type's schedule silently replace the 1st type's badge on the
// card even though both were still pending — this renders every type independently instead.
function ScheduledActivityBadges({ lead }) {
  const acts = pendingActivitiesByType(lead);
  if (!acts.length) return null;
  return acts.map((act) => {
    const meta = SCHEDULED_ACTIVITY_TYPES[act.type];
    if (!meta) return null;
    const Icon = meta.icon;
    const d = new Date(act.scheduled_at);
    const overdue = d.getTime() < Date.now();
    const label = d.toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    return (
      <span key={act.activity_id} className={`inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded-full font-medium ${overdue ? "bg-red-500/15 text-red-400 border border-red-500/30" : ""}`}
        style={overdue ? undefined : { background: meta.accent + "20", color: meta.accent, border: `1px solid ${meta.accent}55` }}
        title={`${meta.label} · ${label}${overdue ? " · terlambat" : ""}`}>
        <Icon size={9.5} />{meta.label} {label}
      </span>
    );
  });
}

// `cardStyle` (padding/marginBottom only, from the mobile Kanban zoom density — see the
// wrapper's own comment) is the ONLY thing that changes with zoom now; every font-size in
// this card stays exactly as declared below, regardless of density level.
function LeadCard({ lead, index, onClick, cardStyle }) {
  const aging = AGING[lead.aging?.level || "none"];
  const [labelOpen, setLabelOpen] = useState(false);
  return (
    <Draggable draggableId={lead.lead_id} index={index}>
      {(prov, snap) => (
        <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
          onClick={onClick} data-testid={`lead-card-${lead.lead_id}`} style={cardStyle}
          className={`relative card-surface border-l-2 ${aging.border} rounded-xl p-3 mb-2.5 cursor-grab active:cursor-grabbing transition-shadow ${snap.isDragging ? "shadow-xl rotate-1 scale-[1.02]" : ""}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold text-sm leading-tight">{lead.nama}</div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY[lead.prioritas] || ""}`}>{lead.prioritas}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><Phone size={11} />{lead.no_hp || "-"}</div>
          {lead.layanan && <div className="text-xs text-zinc-400 mt-1">{lead.layanan}</div>}
          <div className="flex items-center flex-wrap gap-1 mt-1.5">
            {(lead.labels || []).map((l) => (
              <span key={l.label_id} className="text-[9.5px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: l.color + "26", color: l.color, border: `1px solid ${l.color}55` }}>{l.text}</span>
            ))}
            <ScheduledActivityBadges lead={lead} />
            <button data-testid={`lead-label-add-${lead.lead_id}`} onClick={(e) => { e.stopPropagation(); setLabelOpen((v) => !v); }}
              className="text-zinc-600 hover:text-blue-400 p-0.5"><Tag size={11} /></button>
            {labelOpen && <LabelPopover lead={lead} onClose={() => setLabelOpen(false)} />}
          </div>
          {lead.estimasi_anggaran > 0 && (
            <div className="text-xs font-bold text-amber-400 mt-1.5 flex items-center gap-1">🎯 Estimasi: {rupiahShort(lead.estimasi_anggaran)}</div>
          )}
          <div className="text-[10px] text-zinc-600 mt-1.5">Masuk {fmtDate(lead.created_at)}</div>
          <div className="flex items-center justify-between gap-2 mt-1.5">
            <span className="flex items-center gap-1 flex-wrap">
              <span className="text-sm font-bold text-emerald-400 font-heading whitespace-nowrap tabular-nums">{rupiahShort(lead.nilai_total_gabungan ?? lead.nilai_deal)}</span>
              {lead.nilai_produksi > 0 && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30 shrink-0">+Produksi</span>}
            </span>
            {(lead.status === "Konsultasi" || lead.status === "SPH") && (
              <span className="text-[10px] flex items-center gap-1 shrink-0" style={{ color: aging.color }}>
                <Clock size={10} />{lead.aging?.days}h {lead.overdue ? "· due" : ""}
              </span>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

const TIER_RANK = { Hot: 0, Warm: 1, Cold: 2 };
const SORT_OPTS = [
  ["terbaru", "Terbaru"], ["nilai", "Nilai Tertinggi"], ["prioritas", "Prioritas (Hot dulu)"], ["nama", "Nama A-Z"],
];
function sortLeads(items, sortBy) {
  const arr = [...items];
  if (sortBy === "nilai") arr.sort((a, b) => (b.nilai_total_gabungan ?? b.nilai_deal ?? 0) - (a.nilai_total_gabungan ?? a.nilai_deal ?? 0));
  else if (sortBy === "prioritas") arr.sort((a, b) => (TIER_RANK[a.ai_score?.tier] ?? 3) - (TIER_RANK[b.ai_score?.tier] ?? 3));
  else if (sortBy === "nama") arr.sort((a, b) => (a.nama || "").localeCompare(b.nama || ""));
  else arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return arr;
}

// Desktop gets a proper full-size table (matches the List view's own columns) with sort +
// tag filter — reusable regardless of which stage/board it opened from, since it's the same
// component either way (Kanban's "Lihat Semua" and List's "Lihat Selengkapnya" both render
// this). Mobile keeps the original compact card-stack untouched, per explicit instruction
// not to change the mobile version — only desktop was ever "cacat" here (opened a small
// modal styled like the mobile view even on a full desktop screen).
// Clickable column header for the desktop List view's Excel-style table — click toggles
// asc/desc, small chevron mirrors the little dropdown-arrow-per-column look from the
// reference spreadsheet without needing an actual per-column filter dropdown.
function SortTh({ label, sortKey, listSort, onSort, className = "" }) {
  const active = listSort.key === sortKey;
  return (
    <th data-testid={`sort-th-${sortKey}`}
      className={`font-medium select-none cursor-pointer whitespace-nowrap py-2.5 px-4 transition-colors ${active ? "text-blue-300 bg-blue-500/10" : "hover:text-zinc-300 hover:bg-white/[0.03]"} ${className}`}
      onClick={() => onSort(sortKey)} title={`Urutkan berdasarkan ${label}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-xs ${active ? "text-blue-300" : "text-zinc-700"}`}>{active ? (listSort.dir === "desc" ? "▲" : "▼") : "▾"}</span>
      </span>
    </th>
  );
}

function StageAllLeadsDialog({ stage, items, onClose, onSelectLead }) {
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("terbaru");
  const [tagFilter, setTagFilter] = useState("all");
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  React.useEffect(() => { if (stage) { setQ(""); setSortBy("terbaru"); setTagFilter("all"); } }, [stage]);

  const allTags = Array.from(new Set(items.flatMap((l) => (l.labels || []).map((lb) => lb.text)))).sort();
  const filtered = sortLeads(items.filter((l) => {
    const t = q.trim().toLowerCase();
    if (t && !(l.nama?.toLowerCase().includes(t) || (l.no_hp || "").includes(t))) return false;
    if (tagFilter !== "all" && !(l.labels || []).some((lb) => lb.text === tagFilter)) return false;
    return true;
  }), sortBy);

  if (isDesktop) {
    return (
      <Dialog open={!!stage} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="bg-[#0f0f12] border-white/10 max-w-4xl w-[90vw] max-h-[85vh] flex flex-col p-0 gap-0" data-testid="stage-all-dialog">
          <DialogHeader className="p-4 pb-3 border-b border-white/5">
            <DialogTitle className="font-heading flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: stage?.accent }} />
              {stage?.id} <span className="text-zinc-500 text-sm font-normal">({items.length})</span>
            </DialogTitle>
            <DialogDescription>Semua lead di tahap ini — bisa dicari, disortir, dan difilter per tag</DialogDescription>
          </DialogHeader>
          <div className="px-4 pt-3 pb-2 shrink-0 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input data-testid="stage-all-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau no HP…"
                className="w-full bg-[#18181B] border border-white/10 rounded-lg h-9 pl-8 pr-2.5 text-xs" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="stage-all-sort" className="bg-[#18181B] border-white/10 h-9 w-auto min-w-[150px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181B] border-white/10">{SORT_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger data-testid="stage-all-tag" className="bg-[#18181B] border-white/10 h-9 w-auto min-w-[130px] text-xs"><Tag size={12} className="mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181B] border-white/10">
                <SelectItem value="all">Semua Tag</SelectItem>
                {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-y-auto px-4 pb-4 flex-1">
            {filtered.length === 0 ? <div className="text-center text-xs text-zinc-600 py-10">Tidak ditemukan</div> : (
              <table className="w-full text-sm">
                <thead><tr className="text-zinc-500 text-xs uppercase border-b border-white/5 sticky top-0 bg-[#0f0f12]">
                  <th className="text-left py-2 font-medium">Nama</th><th className="text-left font-medium">No HP</th>
                  <th className="text-left font-medium">Tag</th><th className="text-left font-medium">Prioritas</th>
                  <th className="text-right font-medium">Nilai</th><th className="text-right font-medium">Follow-up</th>
                </tr></thead>
                <tbody>
                  {filtered.map((l) => {
                    const aging = AGING[l.aging?.level || "none"];
                    return (
                      <tr key={l.lead_id} data-testid={`stage-all-lead-${l.lead_id}`} onClick={() => { onSelectLead(l.lead_id); onClose(); }}
                        className={`border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/[0.03] border-l-2 ${aging.border}`}>
                        <td className="py-2 pl-2 font-medium">{l.nama}</td>
                        <td className="text-zinc-400">{l.no_hp || "-"}</td>
                        <td className="max-w-[160px]">
                          <div className="flex flex-wrap gap-1">
                            {(l.labels || []).slice(0, 2).map((lb) => (
                              <span key={lb.label_id} className="text-[9px] px-1.5 py-0.5 rounded-full border" style={{ color: lb.color, borderColor: lb.color + "55", background: lb.color + "15" }}>{lb.text}</span>
                            ))}
                          </div>
                        </td>
                        <td><span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY[l.prioritas] || ""}`}>{l.prioritas}</span></td>
                        <td className="text-right font-semibold text-emerald-400 tabular-nums">{rupiahShort(l.nilai_total_gabungan ?? l.nilai_deal)}</td>
                        <td className="text-right text-xs" style={{ color: l.overdue ? "#EF4444" : "#a1a1aa" }}>{fmtDate(l.follow_up_date)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={!!stage} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0f0f12] border-white/10 max-w-lg max-h-[85vh] flex flex-col p-0 gap-0" data-testid="stage-all-dialog">
        <DialogHeader className="p-4 pb-3 border-b border-white/5">
          <DialogTitle className="font-heading flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: stage?.accent }} />
            {stage?.id} <span className="text-zinc-500 text-sm font-normal">({items.length})</span>
          </DialogTitle>
          <DialogDescription>Semua lead di tahap ini, tidak dibatasi tinggi kolom</DialogDescription>
        </DialogHeader>
        <div className="px-4 pt-3 pb-1 shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="stage-all-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau no HP…"
              className="w-full bg-[#18181B] border border-white/10 rounded-lg h-9 pl-8 pr-2.5 text-xs" />
          </div>
        </div>
        <div className="overflow-y-auto px-4 pb-4 pt-2 flex-1">
          {filtered.length === 0 && <div className="text-center text-xs text-zinc-600 py-10">Tidak ditemukan</div>}
          {filtered.map((l) => {
            const aging = AGING[l.aging?.level || "none"];
            return (
              <div key={l.lead_id} data-testid={`stage-all-lead-${l.lead_id}`} onClick={() => { onSelectLead(l.lead_id); onClose(); }}
                className={`card-surface border-l-2 ${aging.border} rounded-xl p-3 mb-2 cursor-pointer hover:bg-white/[0.03] active:bg-white/[0.05]`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-sm leading-tight">{l.nama}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${PRIORITY[l.prioritas] || ""}`}>{l.prioritas}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><Phone size={11} />{l.no_hp || "-"}</div>
                <div className="flex items-center justify-between mt-1.5 gap-2">
                  <span className="text-xs font-medium text-emerald-400 whitespace-nowrap tabular-nums">{rupiahShort(l.nilai_total_gabungan ?? l.nilai_deal)}</span>
                  <span className="text-[10px] text-zinc-600 shrink-0">{fmtDate(l.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Pipeline() {
  const { user } = useAuth();
  const { master, branches, directory } = useMeta();
  const branchName = React.useMemo(() => Object.fromEntries((branches || []).map((b) => [b.branch_id, b.name])), [branches]);
  const salesName = React.useMemo(() => Object.fromEntries((directory || []).map((u) => [u.user_id, u.name])), [directory]);
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "list" : "kanban"));
  const [period, setPeriod] = useState("all");
  const [lostPending, setLostPending] = useState(null); // { leadId, nama, prevStatus }
  const [expandedStage, setExpandedStage] = useState(null);
  // Mobile density is real CSS `zoom` for Kanban on every device including iOS (confirmed
  // correct there too — a brief iOS-only alternate for Kanban, fixed font + computed
  // padding/width instead of zoom, made cards look messy: fixed-size text doesn't fit a
  // narrower column the way real zoom's proportional shrink does). List view is the one
  // exception: WebKit shrinks that table's column widths under `zoom` without shrinking the
  // rendered font to match, so density there comes from a separate iOS-only mechanism
  // (row padding only, font/columns fixed — see the comment where it's applied). Android's
  // List keeps real `zoom` too, same as Kanban. `isIOS` below drives both that List
  // mechanism and one extra fix (Nama column `sticky` disabled on iOS mobile, a known
  // WebKit `sticky`+`zoom` offset quirk).
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const [zoom, setZoom] = useState(() => {
    if (typeof window === "undefined") return 45;
    return Number(localStorage.getItem("equity_pipeline_zoom")) || 45;
  });
  // Takes a delta (+10/-10), not an absolute target — using the functional setState form
  // so two rapid clicks (or a held-down button) each read the truly-latest value instead
  // of both closing over the same stale `zoom` from the render they were created in, which
  // silently ate every other click.
  const zoomBy = (delta) => {
    setZoom((prev) => {
      const clamped = Math.max(40, Math.min(160, prev + delta));
      try { localStorage.setItem("equity_pipeline_zoom", String(clamped)); } catch { /* best-effort */ }
      return clamped;
    });
  };
  // Separate zoom + localStorage key for desktop Kanban — defaults to 80% on first open
  // (fits more of the pipeline right away without the user having to zoom out manually),
  // fully customizable afterwards via the same +/- control, persisted independently of
  // mobile's own zoom preference (they're genuinely different screens with different
  // defaults).
  const [desktopKanbanZoom, setDesktopKanbanZoom] = useState(() => {
    if (typeof window === "undefined") return 80;
    return Number(localStorage.getItem("equity_pipeline_desktop_zoom")) || 80;
  });
  const desktopZoomBy = (delta) => {
    setDesktopKanbanZoom((prev) => {
      const clamped = Math.max(60, Math.min(160, prev + delta));
      try { localStorage.setItem("equity_pipeline_desktop_zoom", String(clamped)); } catch { /* best-effort */ }
      return clamped;
    });
  };
  // Desktop List view — rows were too tall/spaced by default ("jarak tulisan atas bawah
  // terlalu jauh"), and had no way to adjust it (List's own zoom control used to be mobile-
  // only). This is a SEPARATE state from both Kanban zooms and mobile's own List density —
  // desktop never reads real CSS `zoom` for this table (Android's mobile zoom mechanism
  // stays untouched, per explicit instruction not to touch it), it's a scoped row-padding
  // <style> rule instead, same technique as iOS mobile's List density but independently
  // tunable. Defaults to 85% (denser than the old fixed padding) instead of 100%, directly
  // addressing "rapatkan" without the user having to touch the control at all.
  const [desktopListZoom, setDesktopListZoom] = useState(() => {
    if (typeof window === "undefined") return 85;
    return Number(localStorage.getItem("equity_pipeline_desktop_list_zoom")) || 85;
  });
  const desktopListZoomBy = (delta) => {
    setDesktopListZoom((prev) => {
      const clamped = Math.max(60, Math.min(140, prev + delta));
      try { localStorage.setItem("equity_pipeline_desktop_list_zoom", String(clamped)); } catch { /* best-effort */ }
      return clamped;
    });
  };
  // Column width itself stays a CONSTANT vw — the zoom% above (applied as real CSS `zoom`
  // on the whole board wrapper, not here) is what makes it visually bigger/smaller.
  // Two separate mechanisms doing the same job was the bug: shrinking only this vw number
  // while cards/fonts stayed full-size made low zoom look cramped/broken instead of just
  // "farther away". 78vw is comfortable single-board width at 100% zoom; at the 60% default
  // that renders as ~47vw effective — the "2-3 boards visible" sweet spot, uniformly scaled.
  const KANBAN_COL_VW = 78;
  // Excel-style column sort for the desktop List view — one global sort applied within
  // each stage's own table (sorting is per-column across the whole board, not per-section).
  const [listSort, setListSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) => setListSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortRows = (rows) => {
    if (!listSort.key) return rows;
    const { key, dir } = listSort;
    const mul = dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = key === "nilai" ? (a.nilai_total_gabungan ?? a.nilai_deal ?? 0) : key === "cabang" ? (branchName[a.branch_id] || "") : key === "sales" ? (salesName[a.sales_id] || "") : (a[key] ?? "");
      const vb = key === "nilai" ? (b.nilai_total_gabungan ?? b.nilai_deal ?? 0) : key === "cabang" ? (branchName[b.branch_id] || "") : key === "sales" ? (salesName[b.sales_id] || "") : (b[key] ?? "");
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
      return String(va).localeCompare(String(vb)) * mul;
    });
  };
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const { data: leads = [], isLoading } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data });
  const readOnly = false;
  const lostReasons = master.alasan_lost || [];

  const inPeriod = (l) => {
    if (period === "all") return true;
    const c = l.created_at || "";
    if (period === "month") return c.slice(0, 7) === ym;
    if (period === "year") return c.slice(0, 4) === ym.slice(0, 4);
    return true;
  };
  const byStage = (s) => leads.filter((l) => l.status === s && inPeriod(l));
  const yearOpts = [0, 1, 2].map((i) => String(now.getFullYear() - i));
  // Last 24 months as {value, label} — replaces a bare <input type="month">, whose native
  // picker renders wildly differently per browser/OS (a cramped, barely-visible widget on
  // desktop Chrome, a full-screen wheel picker that breaks the dark theme on iOS Safari) and
  // was the "dropdown bulan cacat" report. A styled Select matches every other dropdown in
  // the app and looks the same everywhere.
  const monthOpts = Array.from({ length: 24 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = `${BULAN_ID[d.getMonth()]} ${d.getFullYear()}`;
    return { value, label };
  });
  // Desktop's List view shows every lead inline, no cap — "Lihat Selengkapnya" opening a
  // small modal only ever made sense on mobile's limited screen height; on desktop there's
  // plenty of room to just render the whole table straight down the page. Mobile keeps its
  // original 10-item cap + modal, unchanged.
  const isDesktopView = typeof window !== "undefined" && window.innerWidth >= 1024;
  const LIST_LIMIT = isDesktopView ? Infinity : 10;

  const moveStatus = async (leadId, newStatus, alasan = null) => {
    try {
      await api.patch(`/leads/${leadId}/status`, { status: newStatus, alasan_lost: alasan });
      toast.success(`Dipindah ke ${newStatus}`);
      qc.invalidateQueries();
    } catch (e) { toast.error(apiErr(e)); qc.invalidateQueries({ queryKey: ["leads"] }); }
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;
    const newStatus = destination.droppableId;
    const lead = leads.find((l) => l.lead_id === draggableId);
    if (newStatus === "Lost") {
      // Backend enforces this too (400 if violated) — checked here first so a New
      // Client/Konsultasi card gets an immediate, specific toast instead of flashing
      // into the Lost column and snapping back after a failed request.
      if (!["Survei", "SPH"].includes(source.droppableId)) {
        toast.error('Lost cuma bisa dari tahap Survei atau SPH — kalau memang belum ada respon, biarkan saja, nanti otomatis masuk Tidak Jelas sesuai jadwal di Pengaturan.');
        return;
      }
      qc.setQueryData(["leads"], (old) => old.map((l) => l.lead_id === draggableId ? { ...l, status: newStatus } : l));
      setLostPending({ leadId: draggableId, nama: lead?.nama || "lead ini", prevStatus: source.droppableId });
      return;
    }
    qc.setQueryData(["leads"], (old) => old.map((l) => l.lead_id === draggableId ? { ...l, status: newStatus } : l));
    moveStatus(draggableId, newStatus);
  };

  const confirmLost = (reason) => {
    const p = lostPending;
    setLostPending(null);
    moveStatus(p.leadId, "Lost", reason);
  };

  const cancelLost = () => {
    const p = lostPending;
    if (p) qc.setQueryData(["leads"], (old) => old.map((l) => l.lead_id === p.leadId ? { ...l, status: p.prevStatus } : l));
    setLostPending(null);
  };

  if (isLoading) return null;

  return (
    <div className="py-4">
      <div className="px-4 md:px-8 mb-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold font-heading tracking-tight">Pipeline</h1>
          <p className="text-sm text-zinc-500">{readOnly ? "Tampilan hanya-baca" : (view === "kanban" ? "Geser kartu antar tahap" : view === "calendar" ? "Tampilan kalender" : "Tampilan daftar")}</p>
        </div>
        <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5">
          <button data-testid="view-kanban" title="Papan" onClick={() => setView("kanban")} className={`p-1.5 rounded-md ${view === "kanban" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}><KanbanIcon size={16} /></button>
          <button data-testid="view-calendar" title="Kalender" onClick={() => setView("calendar")} className={`p-1.5 rounded-md ${view === "calendar" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}><CalendarDays size={16} /></button>
          <button data-testid="view-list" title="Daftar" onClick={() => setView("list")} className={`p-1.5 rounded-md ${view === "list" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}><LayoutList size={16} /></button>
        </div>
      </div>

      <div className="px-4 md:px-8 mb-3 flex flex-wrap items-center gap-2">
        <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5">
          {[["all", "Semua"], ["month", "Bulan"], ["year", "Tahun"]].map(([v, l]) => (
            <button key={v} data-testid={`pipeline-period-${v}`} onClick={() => setPeriod(v)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium ${period === v ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>{l}</button>
          ))}
        </div>
        {/* Kanban gets zoom on every device now (desktop has its own separate zoom state/
            default, see desktopKanbanZoom above). List keeps zoom mobile-only — desktop's
            List is already a full unlimited table, zooming that out doesn't help the way
            it helps a card grid. */}
        {view === "kanban" && (
          <div className="flex items-center bg-[#18181B] rounded-lg border border-white/5 ml-auto">
            <button data-testid="pipeline-zoom-out" onClick={() => (isDesktopView ? desktopZoomBy(-10) : zoomBy(-10))} className="w-8 h-7 flex items-center justify-center text-zinc-400 hover:text-white text-sm font-bold">−</button>
            <span className="text-[10px] text-zinc-500 w-9 text-center tabular-nums">{isDesktopView ? desktopKanbanZoom : zoom}%</span>
            <button data-testid="pipeline-zoom-in" onClick={() => (isDesktopView ? desktopZoomBy(10) : zoomBy(10))} className="w-8 h-7 flex items-center justify-center text-zinc-400 hover:text-white text-sm font-bold">+</button>
          </div>
        )}
        {view === "list" && (
          <div className="flex items-center bg-[#18181B] rounded-lg border border-white/5 ml-auto">
            <button data-testid="pipeline-zoom-out" onClick={() => (isDesktopView ? desktopListZoomBy(-10) : zoomBy(-10))} className="w-8 h-7 flex items-center justify-center text-zinc-400 hover:text-white text-sm font-bold">−</button>
            <span className="text-[10px] text-zinc-500 w-9 text-center tabular-nums">{isDesktopView ? desktopListZoom : zoom}%</span>
            <button data-testid="pipeline-zoom-in" onClick={() => (isDesktopView ? desktopListZoomBy(10) : zoomBy(10))} className="w-8 h-7 flex items-center justify-center text-zinc-400 hover:text-white text-sm font-bold">+</button>
          </div>
        )}
        {period === "month" && (
          <Select value={ym} onValueChange={setYm}>
            <SelectTrigger data-testid="pipeline-month" className="bg-[#18181B] border-white/10 h-8 w-auto min-w-[130px] text-xs px-2.5"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10">
              {monthOpts.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {period === "year" && (
          <Select value={ym.slice(0, 4)} onValueChange={(v) => setYm(`${v}-01`)}>
            <SelectTrigger data-testid="pipeline-year" className="bg-[#18181B] border-white/10 h-8 w-auto min-w-[90px] text-xs px-2.5"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10">
              {yearOpts.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {view === "calendar" ? (
        <PipelineCalendar leads={leads.filter(inPeriod)} onSelectLead={setSelected} />
      ) : view === "list" ? (
        <div className="px-3 md:px-8">
          {/* List view's iOS density is its own case, separate from Kanban's (which stays on
              real `zoom` for every device, including iOS — that one's confirmed correct).
              Here specifically, WebKit shrank column widths under `zoom` without shrinking
              the rendered font to match, so a name that fit on one line at 100% wrapped
              across 2-3 lines at low zoom% — this scoped <style> rule (iOS mobile only) is
              the fix: row padding alone carries the density, font size and column widths
              (min-w-[Npx] below) never change, so nothing can wrap differently than it does
              at 100%. Android's List keeps real `zoom` on the wrapper below, unaffected. */}
          {!isDesktopView && isIOS && (
            <style>{`.pl-mobile-table td, .pl-mobile-table th { padding-top: ${(12 * zoom / 100).toFixed(1)}px; padding-bottom: ${(12 * zoom / 100).toFixed(1)}px; }`}</style>
          )}
          {/* Desktop-only row density, same scoped-<style> technique as iOS mobile above but
              a fully separate control (desktopListZoom) — never touches Android's real-zoom
              mechanism, never touches Kanban. */}
          {isDesktopView && (
            <style>{`.pl-desktop-table td, .pl-desktop-table th { padding-top: ${(12 * desktopListZoom / 100).toFixed(1)}px; padding-bottom: ${(12 * desktopListZoom / 100).toFixed(1)}px; }`}</style>
          )}
          {STAGES.map((stage) => {
            const items = byStage(stage.id);
            if (items.length === 0) return null;
            const visible = items.slice(0, LIST_LIMIT);
            const hiddenCount = items.length - visible.length;
            return (
              <div key={stage.id} className="mb-5">
                <div className="flex items-center gap-2 px-1 mb-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: stage.accent }} />
                  <span className="font-semibold text-sm">{stage.id}</span>
                  <span className="text-xs text-zinc-500">({items.length}) · {rupiahShort(items.reduce((a, l) => a + (l.nilai_total_gabungan ?? l.nilai_deal ?? 0), 0))}</span>
                </div>
                <div className="card-surface rounded-2xl overflow-hidden">
                  {/* Hairline gradient accent, darkened and faded into black — a quiet
                      cue instead of a bright flat-color border. */}
                  <div className="h-px" style={{ background: STAGE_BAR_GRADIENT[stage.id] || `linear-gradient(90deg, ${stage.accent}, rgba(0,0,0,0) 68%)`, opacity: 0.8 }} />
                  {/* Excel-style: more columns (cabang/sales/sumber/kategori/kota/detail —
                      same fields the Excel tracker import template uses), every header
                      click-sortable, and the Nama column pinned (position: sticky) so it
                      stays on screen while scrolling right for the rest — no more scrolling
                      back and forth just to remember which row you're looking at. */}
                  {/* Android: real CSS `zoom` on this wrapper, same as always. iOS: no zoom
                      here at all — its density comes entirely from the scoped <style> above
                      instead (see comment there for why). Desktop: never zoomed. */}
                  <div className="overflow-x-auto hide-scrollbar" style={{ zoom: (!isDesktopView && !isIOS) ? `${zoom}%` : undefined }}>
                  <table className={`text-sm border-collapse ${(!isDesktopView && isIOS) ? "pl-mobile-table" : ""} ${isDesktopView ? "pl-desktop-table" : ""}`} style={{ tableLayout: "auto" }}>
                    <thead><tr className="text-zinc-500 text-xs uppercase border-b border-white/5">
                      {/* `sticky` is disabled only for iOS mobile — combined with the CSS
                          `zoom` iOS's own List density used to read from, Safari rendered the
                          sticky Nama column at the wrong offset (a known WebKit zoom+sticky
                          quirk). iOS no longer uses zoom for this table at all, but sticky
                          stays off there regardless to fully rule that quirk out. Desktop and
                          Android both keep sticky — desktop never zoomed here, and Android's
                          real `zoom` doesn't have this WebKit-specific bug. */}
                      <SortTh label="Nama" sortKey="nama" listSort={listSort} onSort={toggleSort} className={`text-left py-2.5 z-10 bg-[#151518] min-w-[180px] ${isIOS && !isDesktopView ? "" : "sticky left-0"}`} />
                      <SortTh label="No HP" sortKey="no_hp" listSort={listSort} onSort={toggleSort} className="min-w-[140px]" />
                      <SortTh label="Cabang" sortKey="cabang" listSort={listSort} onSort={toggleSort} className="min-w-[150px]" />
                      <SortTh label="Sales" sortKey="sales" listSort={listSort} onSort={toggleSort} className="min-w-[140px]" />
                      <SortTh label="Sumber" sortKey="sumber_info" listSort={listSort} onSort={toggleSort} className="min-w-[130px]" />
                      <SortTh label="Kategori" sortKey="kategori_customer" listSort={listSort} onSort={toggleSort} className="min-w-[130px]" />
                      <SortTh label="Layanan" sortKey="layanan" listSort={listSort} onSort={toggleSort} className="min-w-[160px]" />
                      <SortTh label="Kota" sortKey="kota" listSort={listSort} onSort={toggleSort} className="min-w-[130px]" />
                      <SortTh label="Detail Order" sortKey="detail_order" listSort={listSort} onSort={toggleSort} className="min-w-[260px]" />
                      <SortTh label="Prioritas" sortKey="prioritas" listSort={listSort} onSort={toggleSort} className="min-w-[120px]" />
                      <SortTh label="Nilai (Gabungan)" sortKey="nilai" listSort={listSort} onSort={toggleSort} className="text-right min-w-[170px]" />
                      <SortTh label="Follow-up" sortKey="follow_up_date" listSort={listSort} onSort={toggleSort} className="text-right min-w-[140px]" />
                    </tr></thead>
                    <tbody>
                      {sortRows(visible).map((l) => {
                        const aging = AGING[l.aging?.level || "none"];
                        return (
                          <tr key={l.lead_id} data-testid={`pipeline-row-${l.lead_id}`} onClick={() => setSelected(l.lead_id)} className={`border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/[0.02] group`}>
                            <td className={`py-3 px-4 font-medium z-10 bg-[#151518] group-hover:bg-[#1a1a1e] border-l-2 ${aging.border} ${isIOS && !isDesktopView ? "" : "sticky left-0"}`}>{l.nama}</td>
                            <td className="text-zinc-400 px-4">{l.no_hp || "-"}</td>
                            <td className="text-zinc-400 px-4">{branchName[l.branch_id] || "-"}</td>
                            <td className="text-zinc-400 px-4">{salesName[l.sales_id] || "-"}</td>
                            <td className="text-zinc-400 px-4">{l.sumber_info || "-"}</td>
                            <td className="text-zinc-400 px-4">{l.kategori_customer || "-"}</td>
                            <td className="text-zinc-400 px-4">{l.layanan || "-"}</td>
                            <td className="text-zinc-400 px-4">{l.kota || "-"}</td>
                            <td className="text-zinc-400 px-4 max-w-[300px] truncate" title={l.detail_order}>{l.detail_order || "-"}</td>
                            <td className="px-4"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY[l.prioritas] || ""}`}>{l.prioritas}</span></td>
                            <td className="text-right font-semibold text-emerald-400 px-4">
                              {rupiah(l.nilai_total_gabungan ?? l.nilai_deal)}
                              {l.nilai_produksi > 0 && <div className="text-[10px] font-normal text-purple-400">Produksi {rupiah(l.nilai_produksi)}</div>}
                              {l.estimasi_anggaran > 0 && <div className="text-[10px] font-bold text-amber-400">🎯 Estimasi {rupiah(l.estimasi_anggaran)}</div>}
                            </td>
                            <td className="text-right px-4 text-xs" style={{ color: l.overdue ? "#EF4444" : "#a1a1aa" }}>{fmtDate(l.follow_up_date)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  {hiddenCount > 0 && (
                    <button data-testid={`list-more-${stage.id}`} onClick={() => setExpandedStage(stage)}
                      className="w-full text-center text-xs font-medium text-blue-300 hover:text-blue-200 py-2.5 border-t border-white/5 bg-white/[0.02] hover:bg-white/[0.04]">
                      Lihat Selengkapnya ({hiddenCount} lainnya)
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <DragDropContext onDragEnd={readOnly ? () => {} : onDragEnd}>
        {/* Real CSS `zoom` for every device, mobile and desktop alike — a fixed column width
            (KANBAN_COL_VW, unchanged by the density%) is enough since the ambient zoom itself
            does all the visual scaling (font, padding, gap, width together). A brief attempt
            at a separate iOS-only mechanism here (fixed font, computed padding/width instead)
            made cards look messy — fixed-size text doesn't fit gracefully into a narrower
            column the way real zoom's proportional shrink does. `zoom` behaves the same on
            iOS as everywhere else for this board; List view's Nama column was the one place
            with an actual iOS-specific issue (sticky + zoom), and that fix stays isolated
            there rather than spreading a whole second mechanism to this board too. */}
        <div className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar px-3 md:px-8 pb-4 gap-3"
          style={{ zoom: `${isDesktopView ? desktopKanbanZoom : zoom}%` }}>
          {STAGES.map((stage) => {
            const items = byStage(stage.id);
            const sum = items.reduce((a, l) => a + (l.nilai_total_gabungan ?? l.nilai_deal ?? 0), 0);
            return (
              <Droppable droppableId={stage.id} key={stage.id}>
                {(prov) => (
                  <div ref={prov.innerRef} {...prov.droppableProps}
                    style={{
                      width: typeof window !== "undefined" && window.innerWidth >= 640 ? undefined : `${KANBAN_COL_VW}vw`,
                      // vh is always relative to the REAL viewport (zoom doesn't change what
                      // "100vh" means), but the whole box then gets scaled again by the
                      // ambient `zoom` on the wrapper — so without this compensation, a board
                      // at low zoom would only reach that fraction of the intended screen
                      // height, leaving dead space below it instead of filling the screen.
                      maxHeight: `calc((100vh - 180px) / ${(isDesktopView ? desktopKanbanZoom : zoom) / 100})`,
                    }}
                    className="snap-center shrink-0 sm:w-[330px] card-surface rounded-2xl flex flex-col">
                    <div className="p-3.5 border-b border-white/5 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: stage.accent }} />
                        <span className="font-semibold text-sm truncate">{stage.id}</span>
                        <span className="text-xs text-zinc-500 shrink-0">({items.length})</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-zinc-400 font-medium">{rupiahShort(sum)}</span>
                        {items.length > KANBAN_LIMIT && (
                          <button data-testid={`stage-expand-${stage.id}`} onClick={() => setExpandedStage(stage)}
                            className="text-[10px] px-1.5 py-1 rounded-md bg-white/5 text-blue-300 hover:bg-white/10 active:bg-white/15 font-medium whitespace-nowrap">
                            Lihat Semua
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="p-2.5 overflow-y-auto hide-scrollbar flex-1" data-testid={`kanban-col-${stage.id}`}>
                      {items.slice(0, KANBAN_LIMIT).map((l, i) => <LeadCard key={l.lead_id} lead={l} index={i} onClick={() => setSelected(l.lead_id)} />)}
                      {prov.placeholder}
                      {items.length === 0 && <div className="text-center text-xs text-zinc-600 py-8">Kosong</div>}
                      {items.length > KANBAN_LIMIT && (
                        <button data-testid={`kanban-more-${stage.id}`} onClick={() => setExpandedStage(stage)}
                          className="w-full text-center text-xs font-medium text-blue-300 hover:text-blue-200 py-2.5 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] mt-1">
                          +{items.length - KANBAN_LIMIT} lainnya · Lihat Semua
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
      )}
      <LeadDetailSheet leadId={selected} onClose={() => setSelected(null)} />

      <button data-testid="add-lead-fab" onClick={() => setAddOpen(true)}
        className="fixed z-40 right-4 md:right-8 h-12 w-12 md:h-auto md:w-auto md:px-5 md:py-2.5 flex items-center justify-center gap-2 rounded-full font-semibold text-white border border-white/10 shadow-lg shadow-black/40"
        style={{ bottom: "calc(6rem + env(safe-area-inset-bottom))", backgroundImage: "linear-gradient(155deg, #1e293b 0%, #0f172a 55%, #020617 100%)" }}>
        <Plus size={18} /><span className="hidden md:inline">Tambah Lead</span>
      </button>
      <LeadFormDialog open={addOpen} onOpenChange={setAddOpen} lead={null} />
      <LostReasonDialog pending={lostPending} reasons={lostReasons} onConfirm={confirmLost} onCancel={cancelLost} />
      <StageAllLeadsDialog stage={expandedStage} items={expandedStage ? byStage(expandedStage.id) : []} onClose={() => setExpandedStage(null)} onSelectLead={setSelected} />
    </div>
  );
}
